import axios, { AxiosResponse } from 'axios'
import { ApiFormId } from 'common.types'
import { useUtilities } from 'hooks'
import { useEffect, useState } from 'react'
import * as Sentry from '@sentry/react'

let serverNonce: string
let hasInitialized = false

export const useApi = (formId?: ApiFormId, pollingOnSubmit?: boolean) => {
  const { wait, randomString } = useUtilities()

  const recordError = (e: any) => {
    console.error(e)
    if (Sentry.isInitialized()) {
      Sentry.captureException(e)
    }
  }

  const WP_API_BASE =
    process.env.REACT_APP_API_BASE_URL + '/wp-json/votepa-api/v1'
  const SERVER_API_BASE = process.env.REACT_APP_SERVER_API_BASE_URL
    ? process.env.REACT_APP_SERVER_API_BASE_URL + '/api/v1'
    : ''
  const [loading, setLoading] = useState(false)

  const [error, setError] = useState('')
  const [recoveredFormState, setRecoveredFormState] = useState<any | undefined>(
    undefined,
  )

  const getFormContent = async () => {
    let response: any
    const url = `${SERVER_API_BASE}/forms/content?cache-bust=`
    const stringLength = 8
    try {
      response = await axios.get(url.concat(randomString(stringLength)))
    } catch (e) {
      try {
        response = await axios.get(url.concat(randomString(stringLength)))
      } catch (e) {
        console.error(e)
      }
    }
    return response?.data?.data
  }

  const refreshServerNonce = async () => {
    if (SERVER_API_BASE) {
      const responseServer = await axios.get(`${SERVER_API_BASE}/forms/token`)
      serverNonce = responseServer?.data?.data?.csrfToken
    }
  }

  useEffect(() => {
    const initialize = async () => {
      if (hasInitialized || !formId) {
        return
      }
      try {
        setLoading(true)
        await wait(500)
        await refreshServerNonce()
        const urlParams = new URLSearchParams(window.location.search)
        const queryFormId = urlParams.get('formId')
        const userId = urlParams.get('recoverFormStateByUserId')
        if (queryFormId && userId) {
          const response = await axios.get(
            `${WP_API_BASE}/${formId}/form-state/${userId}`,
          )
          const formState = response?.data?.data?.formState
          if (formState) {
            setRecoveredFormState(formState)
          }
        }
        setLoading(false)
        hasInitialized = true
      } catch (e) {
        try {
          await wait(500)
          await refreshServerNonce()
        } catch (e) {
          if (formId === 'voter-registration') {
            window.location.reload()
            return
          } else {
            recordError(e)
            setError('Cannot initialize form.')
            setLoading(false)
          }
        }
      }
    }
    initialize()
  }, [hasInitialized])

  const sendPollingRequest = async (pollingId: string) => {
    const response = await axios.get(`${SERVER_API_BASE}/polling/submission`, {
      params: {
        pollingId,
      },
      headers: {
        'csrf-token': serverNonce,
      },
    })
    return response
  }

  const submitForm = async (data: any) => {
    const sendSubmit = async (data: any) => {
      setLoading(true)
      let serverResponse: AxiosResponse | undefined
      let overallError = false
      if (serverNonce) {
        try {
          serverResponse = await axios.post(
            `${SERVER_API_BASE}/forms/${formId}/submit`,
            {
              ...data,
            },
            {
              headers: {
                'csrf-token': serverNonce,
              },
            },
          )
        } catch (e) {
          await wait(500)
          try {
            serverResponse = await axios.post(
              `${SERVER_API_BASE}/forms/${formId}/submit`,
              {
                ...data,
              },
              {
                headers: {
                  'csrf-token': serverNonce,
                },
              },
            )
          } catch (e) {
            Sentry.captureException(e)
          }
        }
        let pollingNow = false
        if (pollingOnSubmit && data.pollingId) {
          serverResponse = undefined
          pollingNow = true
          setTimeout(() => {
            if (pollingNow) {
              setError(
                'Form submission timed out at 2 minutes. Please try again later.',
              )
              pollingNow = false
              overallError = true
            }
          }, 60 * 1000 * 2)
          while (pollingNow) {
            try {
              const response = await sendPollingRequest(data.pollingId)
              if (response?.data?.success) {
                serverResponse = response
                pollingNow = false
              }
            } catch (e) {
              try {
                await refreshServerNonce()
                const response = await sendPollingRequest(data.pollingId)
                if (response?.data?.success) {
                  serverResponse = response
                  pollingNow = false
                }
              } catch (e) {
                pollingNow = false
                setError('Cannot submit form. Please try again later.')
                console.error(e)
              }
            }
            await wait(3 * 1000)
          }
        }
      }
      setLoading(false)
      if (overallError) {
        return false
      }
      return { serverResponse }
    }
    try {
      return await sendSubmit(data)
    } catch (e) {
      recordError(e)
      try {
        await wait(500)
        await refreshServerNonce()
        return await sendSubmit(data)
      } catch (e) {
        recordError(e)
        setLoading(false)
        setError('Cannot submit form. Please try again later.')
        return false
      }
    }
  }

  const triggerFormEvent = async (data: any) => {
    const sendFormEvent = async (data: any) => {
      setLoading(true)
      const response = await axios.post(
        `${SERVER_API_BASE}/forms/${formId}/event`,
        {
          ...data,
        },
        {
          headers: {
            'csrf-token': serverNonce,
          },
        },
      )
      setLoading(false)
      return { serverResponse: response }
    }
    try {
      return await sendFormEvent(data)
    } catch (e) {
      console.error(e)
      try {
        await wait(500)
        return await sendFormEvent(data)
      } catch (e) {
        console.error(e)
        setLoading(false)
        setError('Cannot submit form. Please try again later.')
        return false
      }
    }
  }

  return {
    submitForm,
    triggerFormEvent,
    getFormContent,
    error,
    loading,
    recoveredFormState,
  }
}
